import {
	type EntryPointOption,
	type Flight,
	type FlightLookupRoute,
	TRAVEL_MODES,
	TRAVEL_TRANSPORT_TYPE_OTHER_MAX,
	TRAVEL_TRANSPORT_TYPES_BY_MODE,
	type TravelTransportType,
	defaultTransportType,
	legNeedsFlightNumber,
	TRAVEL_ORIGIN_MAX,
	TRAVEL_REMARKS_MAX,
	TRAVEL_SERVICE_NO_MAX,
	type TravelDirection,
	type TravelMode,
	formatFlightDesignator,
	parseFlightDesignator,
} from "@seagames/contracts";
import { Icons, Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Fragment, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { entryPointOptionsOptions } from "../../lib/api/entry-points";
import { formatDayShort, wallTimeIn, wallTimeToIso, zoneAbbreviation } from "../../lib/format/datetime";
import { DateInput } from "../common/date-input";
import { DatePickerCalendar } from "../common/date-picker-calendar";
import { AirlineLogosProvider, useAirlineLogos } from "../travel/airline-logo";
import { AirportSelect } from "../travel/airport-select";
import { F, Pair, SwitchRow, TextArea, TextInput, inputClass } from "../accreditation/register/parts";
import { FlightCard } from "./flight-status";
import { type FlightLookupState, useFlightDates, useFlightLookup } from "./hooks";
import { ModeIcon } from "./icons";
import { ChainError, ChainGap, ChainStep, ConnectionRow, Junction, LinkButton } from "./transit";
import {
	type AirDateMode,
	type ConnectionDraft,
	type PickedDayMiss,
	SCHEDULE_FIELDS,
	type TravelLegDraft,
	type TravelLegErrors,
	type TypedDateReason,
	airDateMode,
	calendarStartFor,
	canAddConnection,
	chainErrorKey,
	chainOrigin,
	connectionPosition,
	draftChain,
	emptyConnection,
	mainPosition,
	designatorKey,
	detailsMustOpen,
	detailsOpenByDefault,
	entryPointNeedsChoice,
	initialManual,
	offersFirstConnection,
	pickedDayMiss,
	terminalLabel,
} from "./model";

export interface TravelLegFormProps {
	/** Labels, the lookup's date rule and the origin's meaning (from / to) follow it. */
	direction: TravelDirection;
	value: TravelLegDraft;
	onChange: (next: TravelLegDraft) => void;
	/** `games.timezone` (IANA). Dates and times are entered and shown in it, labelled "MYT". */
	timeZone: string;
	errors?: TravelLegErrors;
	/** DOM ids are `${idPrefix}-${field}` (`rg-arrival-travel_date`), so a caller can focus a field after a failed save. */
	idPrefix: string;
	disabled?: boolean;
}

/**
 * One arrival or departure — her NOC "New arrival" sheet (`ntSheet`,
 * l3.js:1773) minus the parts that belong to a schedule, not a person
 * (passengers, bags, booking ref).
 *
 * AIR leads with the ticket: travel mode, then the flight number (required)
 * and the flight date. Blur or Enter on the number asks which days it flies
 * (`GET /flights/dates`) and the date becomes a calendar where only those
 * days can be picked — the flight's DEPARTURE date, labelled so for an
 * arrival, which may land the next day. A picked day is looked up
 * (`date_kind=SERVICE`) and fills everything: the leg's date (the local day
 * it lands / leaves), time, origin and entry point (by terminal). The number
 * and date then give way to the flight's card (route, times; "Change" brings
 * them back with the calendar open). Time and origin are the flight's and
 * are not typed; the entry point shows only when the Games airport has more
 * than one (a terminal guess to check). The modes of the date field are
 * `airDateMode` (model.ts).
 *
 * A picked day the lookup cannot turn into a flight (it flies that day but
 * not via a Games airport — the dates know no routes — or not at all after
 * all, or the provider is down / rate-limited) keeps the calendar, with the
 * reason under it and "Enter times yourself" into the typed fallback. A
 * flight that does not reach a Games airport offers "+ Add a connecting
 * flight" there; a found flight offers it under its card (transit).
 *
 * The lookup is an accelerator, never a gate: tracking off, a flight the
 * schedules do not carry (charters), the provider down or rate-limited, a
 * number that is not a flight number — the field says so in one line and
 * the plain typed date, time, entry point and origin open (and the typed-date
 * lookup on blur still applies). An existing hand-typed leg opens that way
 * too. An existing leg on a flight shows its departure date; opening the
 * calendar fetches the days, and if they cannot be fetched the date stays
 * read-only with a "Change" into the typed fallback.
 *
 * LAND / SEA: mode, entry point, date, time (optional: "not known yet" is a
 * valid answer), then the coach / ferry and where from. All modes end with
 * the transfer switch and remarks (behind "+ Add a remark" until there is one). Controlled: the caller owns the draft and
 * decides when to save (no autosave — every save is an audit row).
 *
 * Shared: the Register flow, the record page's Edit sheet, and the A&D
 * page's "Add travel" bulk sheet (M9 S4).
 */
export function TravelLegForm({ direction, value, onChange, timeZone, errors = {}, idPrefix, disabled = false }: TravelLegFormProps) {
	const { t } = useTranslation("travel");
	const options = useQuery(entryPointOptionsOptions());
	const { state: lookupState, lookup, reset } = useFlightLookup(direction);
	// A ticketed flight (a commercial one, or an older leg that never said): looked up by its number.
	// A private / cargo or other flight is typed in like a coach — no lookup, no number required.
	const air = value.mode === "AIR" && legNeedsFlightNumber("AIR", value.transport_type);
	const dates = useFlightDates(direction, air ? value.service_no : "");
	const arrival = direction === "ARRIVAL";
	const zone = zoneAbbreviation(timeZone);
	const id = (f: string) => `${idPrefix}-${f}`;

	// The typed fallback, chosen for ONE flight number ("Change", a picked day the lookup could not
	// answer, an existing hand-typed leg). Another number is asked afresh.
	const [manual, setManual] = useState<{ key: string; reason: TypedDateReason } | null>(() => {
		const reason = initialManual(value);
		return reason ? { key: designatorKey(value.service_no), reason } : null;
	});
	const manualReason = manual && manual.key === designatorKey(value.service_no) ? manual.reason : null;
	// The last picked day the lookup could not answer (kept for its number only; the calendar stays).
	const [pickMiss, setPickMiss] = useState<PickedDayMiss | null>(null);
	const dateMode: AirDateMode | null = air ? airDateMode(value, { committed: dates.committed, dates: dates.outcome, manual: manualReason, pickMiss }) : null;
	const typedDate = !air || dateMode?.kind === "typed";

	// AIR: entry point (and, with no flight, time and origin) are hidden until needed. They open by
	// themselves when the person has to see something there (a miss, typed dates, an error, an existing
	// hand-typed leg), and never close by themselves. No toggle: "Enter times yourself" is the way in.
	const [detailsOpen, setDetailsOpen] = useState(() => detailsOpenByDefault(value, { kind: "idle" }, errors, dateMode ?? undefined));
	const mustOpen = detailsMustOpen(value, lookupState, errors, dateMode ?? undefined);
	useEffect(() => {
		if (mustOpen) setDetailsOpen(true);
	}, [mustOpen]);

	// The calendar: inline under the number and date (a popover would be clipped by the bulk sheet).
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [calendarFocus, setCalendarFocus] = useState(false);
	const dateButton = useRef<HTMLButtonElement>(null);
	/** The departure day being looked up, shown as picked until the flight answers. */
	const [picking, setPicking] = useState<string | null>(null);
	const pickSeq = useRef(0);
	const pickedDay = picking ?? value.flight?.service_date ?? null;
	// The days arrived for a number with nothing picked yet: show them without being asked.
	const offerCalendar = dateMode?.kind === "picker" && !value.flight && !picking;
	useEffect(() => {
		if (offerCalendar) {
			setCalendarFocus(false);
			setCalendarOpen(true);
		}
	}, [offerCalendar]);
	// A found flight shows as its card; "Change" (or an error on the number or date) brings the fields back.
	const [editing, setEditing] = useState(false);
	const flightFieldsShown = !value.flight || editing || !!(errors.service_no ?? errors.flight_id ?? errors.travel_date);
	const showCalendar = calendarOpen && dateMode?.kind === "picker" && !disabled && flightFieldsShown;

	// Remarks sit behind "+ Add a remark" until there are some; once shown they stay (clearing the text must not fold it mid-typing).
	const [remarksOpen, setRemarksOpen] = useState(() => value.remarks.trim() !== "");
	const [remarksFocus, setRemarksFocus] = useState(false);
	useEffect(() => {
		if (value.remarks.trim() !== "") setRemarksOpen(true);
	}, [value.remarks]);
	const showRemarks = remarksOpen || !!errors.remarks;

	// The time is typed as a wall clock in the games' zone and stored as an instant. Held locally too,
	// so a time typed before the date is not lost (it becomes an instant once the date is there).
	const [time, setTime] = useState(() => wallTimeIn(value.scheduled_at, timeZone)?.time ?? "");
	useEffect(() => {
		if (value.scheduled_at) setTime(wallTimeIn(value.scheduled_at, timeZone)?.time ?? "");
	}, [value.scheduled_at, timeZone]);

	// The latest draft, for a lookup that answers after the person kept typing: its fill merges into
	// what is there now, not into the draft the blur saw.
	const latest = useRef(value);
	latest.current = value;

	/** Every change goes through here: a new schedule drops a host's manual delay. */
	const set = (patch: Partial<TravelLegDraft>) => {
		const base = latest.current;
		const next = { ...base, ...patch };
		if (SCHEDULE_FIELDS.some((k) => k in patch && patch[k] !== base[k])) next.delayed_to = null;
		latest.current = next;
		onChange(next);
	};

	const ofMode = (m: TravelMode | ""): EntryPointOption[] => (options.data ?? []).filter((o) => o.type === m);

	const setMode = (mode: TravelMode) => {
		if (mode === value.mode) return;
		reset();
		setCalendarOpen(false);
		const same = ofMode(mode);
		set({
			mode,
			transport_type: defaultTransportType(mode),
			transport_type_other: "",
			// An entry point of another type cannot carry this leg (the API refuses the mismatch).
			entry_point: value.entry_point?.type === mode ? value.entry_point : same.length === 1 ? same[0]! : null,
			flight_id: null,
			flight: null,
			service_no: "",
			connections: [],
			// Her rule (`ntSheet`): a transfer is the default, except overland — coaches usually drive on.
			needs_transport: mode !== "LAND",
		});
	};

	/** Commercial flight / Car / Cruise…; leaving a ticketed flight drops what the lookup found. */
	const setTransportType = (transport_type: TravelTransportType) => {
		if (transport_type === value.transport_type) return;
		const ticketed = value.mode === "AIR" && legNeedsFlightNumber("AIR", transport_type);
		if (air && !ticketed) {
			reset();
			setCalendarOpen(false);
			set({ transport_type, flight_id: null, flight: null, connections: [] });
		} else set({ transport_type });
	};

	/** The typed date (the fallback, and land / sea). */
	const setDate = (travel_date: string) => {
		const scheduled_at = time && travel_date ? wallTimeToIso(travel_date, time, timeZone) : null;
		// A flight is one date's flight: another date is another row (looked up again on blur).
		const detach = value.flight_id !== null && travel_date !== value.travel_date;
		if (detach) reset();
		set({ travel_date, scheduled_at, ...(detach ? { flight_id: null, flight: null } : {}) });
	};

	const setTimeText = (v: string) => {
		setTime(v);
		set({ scheduled_at: v && value.travel_date ? wallTimeToIso(value.travel_date, v, timeZone) : null });
	};

	// ── Transit: the connecting flights around the main one ──
	const transit = air && value.connections.length > 0;
	const chain = draftChain(direction, value);
	const mainPos = mainPosition(direction, value.connections.length);
	const toDay = (iso: string) => wallTimeIn(iso, timeZone)?.date ?? null;
	const notOurAirport = (lookupState.kind === "miss" && lookupState.reason === "NOT_OUR_AIRPORT") || (dateMode?.kind === "picker" && dateMode.miss?.reason === "NOT_OUR_AIRPORT");

	/** Every change of the chain: the leg's origin (ARRIVAL) / destination (DEPARTURE) follows its ends. */
	const setConnections = (connections: ConnectionDraft[], extra: Partial<TravelLegDraft> = {}) => {
		const place = chainOrigin(direction, { ...latest.current, ...extra, connections });
		set({ ...extra, connections, ...(place ? { origin: place } : {}) });
	};
	/** "+ Add a connecting flight": the first connection row. */
	const startTransit = () => {
		// The number in the main field does not reach a Games airport: it is the flight they connect from
		// (ARRIVAL) / on to (DEPARTURE). Move it into the first connection; the main field is for the other.
		if (notOurAirport && value.service_no.trim() && !value.flight_id) {
			const seed = value.service_no;
			reset();
			pickSeq.current++;
			setPicking(null);
			setPickMiss(null);
			setCalendarOpen(false);
			setTime("");
			setConnections([emptyConnection(seed)], { service_no: "", flight_id: null, flight: null, travel_date: "", scheduled_at: null, origin: "" });
			return;
		}
		setConnections([emptyConnection()]);
	};
	// By key, not index: a lookup may answer after a row above was removed.
	const updateConnection = (next: ConnectionDraft) => setConnections(latest.current.connections.map((c) => (c.key === next.key ? next : c)));
	const removeConnection = (index: number) => setConnections(latest.current.connections.filter((_, i) => i !== index));
	const addConnection = () => setConnections([...latest.current.connections, emptyConnection()]);

	/** A hit fills what the flight knows. Time and origin are the flight's; the entry point stays editable. */
	const applyFlight = (flight: Flight, suggestedEntryPointId: string | null) => {
		setEditing(false);
		const at = arrival ? flight.scheduled_arrival_at : flight.scheduled_departure_at;
		const wall = wallTimeIn(at, timeZone);
		const suggested = suggestedEntryPointId ? (options.data ?? []).find((o) => o.entry_point_id === suggestedEntryPointId && o.type === "AIR") : undefined;
		// A transit leg comes from its first flight's origin / goes to its last flight's destination.
		const place = chainOrigin(direction, { flight, connections: latest.current.connections }) ?? (arrival ? (flight.origin_name ?? flight.origin_iata) : flight.destination_iata);
		setTime(wall?.time ?? "");
		set({
			flight_id: flight.flight_id,
			flight,
			service_no: formatFlightDesignator(flight.carrier_code, flight.flight_number),
			// The leg's date is the day it lands (arrival) / leaves (departure) in the games' zone.
			...(at && wall ? { scheduled_at: at, travel_date: wall.date } : !arrival ? { scheduled_at: null, travel_date: flight.service_date } : { scheduled_at: null }),
			...(suggested ? { entry_point: suggested } : {}),
			...(place ? { origin: place } : {}),
		});
	};

	/** The typed-date lookup (`date_kind=LEG`): the fallback's accelerator, as before the calendar. */
	const runLookup = async (date = value.travel_date) => {
		if (value.mode !== "AIR" || disabled) return;
		if (value.flight_id && value.flight && date === value.travel_date) return; // already on this flight
		const typed = value.service_no;
		const answer = await lookup(typed, date);
		// Answered after the number was changed again: not this leg's flight any more.
		if (latest.current.service_no !== typed || latest.current.mode !== "AIR") return;
		if (answer?.result?.found && answer.result.flight) applyFlight(answer.result.flight, answer.result.suggested_entry_point_id);
	};

	/** A day picked in the calendar: the flight's departure date, looked up as such. */
	const pickDay = async (day: string) => {
		setCalendarOpen(false);
		dateButton.current?.focus();
		if (day === value.flight?.service_date) {
			setEditing(false); // the same flight again: back to its card
			return;
		}
		const seq = ++pickSeq.current;
		setPicking(day);
		const typed = value.service_no;
		const answer = await lookup(typed, day, "SERVICE");
		if (seq !== pickSeq.current) return; // another day was picked meanwhile
		setPicking(null);
		if (!answer || latest.current.service_no !== typed || latest.current.mode !== "AIR") return;
		if (answer.result?.found && answer.result.flight) {
			setPickMiss(null);
			applyFlight(answer.result.flight, answer.result.suggested_entry_point_id);
			return;
		}
		const key = designatorKey(typed);
		const failure = answer.state.kind === "miss" && answer.state.reason === "RATE_LIMITED" ? "RATE_LIMITED" : "UNAVAILABLE";
		const next = pickedDayMiss(key, day, answer.result, failure);
		// A flight on the leg was another day's: it no longer answers what was picked.
		const fromFlight = !!latest.current.flight_id;
		if (fromFlight) setTime("");
		if (next.kind === "typed") {
			// Tracking was switched off since the days were fetched: type it (never a gate). A departure's
			// leg date IS the departure date; an arrival's may be the next day, so it is asked.
			setPickMiss(null);
			setManual({ key, reason: next.reason });
			set({ flight_id: null, flight: null, scheduled_at: null, travel_date: arrival ? "" : day, ...(fromFlight ? { origin: "" } : {}) });
			return;
		}
		// The schedules list the day but the flight could not be had: stay on the calendar, say why.
		setPickMiss(next.miss);
		if (fromFlight) set({ flight_id: null, flight: null, scheduled_at: null, travel_date: "", origin: "" });
		setCalendarFocus(false);
		setCalendarOpen(true);
	};

	/** "Enter it yourself instead" under a picked day's miss: the typed fallback for this number. */
	const enterYourself = () => {
		const day = dateMode?.kind === "picker" ? dateMode.miss?.day : undefined;
		setManual({ key: designatorKey(value.service_no), reason: "MANUAL" });
		setPickMiss(null);
		setCalendarOpen(false);
		reset();
		if (!arrival && day && !value.travel_date) set({ travel_date: day });
	};

	/** Blur / Enter on the flight number: ask which days it flies (once per number, cached). */
	const commitFlight = () => {
		if (!air || disabled) return;
		dates.commit(value.service_no);
		// Already typing this number by hand: the typed-date lookup still applies.
		if (dateMode?.kind === "typed" && (manualReason || dates.committed)) void runLookup();
	};

	const onFlightKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") return;
		e.preventDefault(); // Enter here means "look it up", never "submit the sheet"
		commitFlight();
	};

	const openCalendar = () => {
		if (dateMode?.kind === "attached") dates.commit(value.service_no); // the days are fetched only now
		setCalendarFocus(true);
		setCalendarOpen((o) => !o);
	};

	/** "Change" on the flight's card: the number and date again, the calendar open on the days it flies. */
	const changeFlight = () => {
		setEditing(true);
		if (dateMode?.kind === "attached") dates.commit(value.service_no);
		if (dateMode?.kind === "picker" || dateMode?.kind === "attached") {
			setCalendarFocus(true);
			setCalendarOpen(true);
		}
	};

	const epOptions: SelectOption[] = (() => {
		const list = ofMode(value.mode);
		const cur = value.entry_point;
		const withCurrent = cur && !list.some((o) => o.entry_point_id === cur.entry_point_id) ? [cur, ...list] : list;
		return withCurrent.map((o) => ({
			value: o.entry_point_id,
			label: o.name,
			description: [o.code, o.iata_code && o.terminal ? t("legForm.flight.terminal", { code: o.iata_code, terminal: terminalLabel(o.terminal) }) : o.iata_code].filter(Boolean).join(" · "),
		}));
	})();

	// `mustOpen` too, not only via the effect: a caller focuses the first bad field on the next frame.
	// On a flight, the entry point shows when the Games airport has more than one (a terminal to check).
	const showDetails = !air || detailsOpen || mustOpen || entryPointNeedsChoice(direction, value, options.data ?? []);
	const serviceLabel =
		value.mode === "SEA" ? t("legForm.field.ferry") : value.mode === "LAND" ? t("legForm.field.coach") : air ? t("legForm.field.flight") : t("legForm.field.callSign");
	const servicePlaceholder =
		value.mode === "SEA" ? t("legForm.placeholder.ferry") : value.mode === "LAND" ? t("legForm.placeholder.coach") : air ? t("legForm.placeholder.flight") : t("legForm.placeholder.callSign");
	const today = wallTimeIn(new Date().toISOString(), timeZone)?.date ?? new Date().toISOString().slice(0, 10);

	const entryField = (
		<F
			label={arrival ? t("legForm.field.arrivesAt") : t("legForm.field.leavesFrom")}
			htmlFor={id("entry_point_id")}
			required
			error={errors.entry_point_id}
			hint={options.isSuccess && epOptions.length === 0 && value.mode ? t("legForm.entryPoint.noneOfMode") : undefined}
		>
			<Select
				id={id("entry_point_id")}
				value={value.entry_point?.entry_point_id ?? null}
				onChange={(v) => set({ entry_point: (options.data ?? []).find((o) => o.entry_point_id === v) ?? (v === value.entry_point?.entry_point_id ? value.entry_point : null) })}
				options={epOptions}
				placeholder={options.isPending ? t("legForm.entryPoint.loading") : t("legForm.entryPoint.placeholder")}
				searchable={epOptions.length > 8}
				searchPlaceholder={t("legForm.entryPoint.search")}
				emptyMessage={t("legForm.entryPoint.noneOfMode")}
				disabled={disabled || !value.mode}
			/>
		</F>
	);

	const typedDateField = (
		<F label={t("legForm.field.date")} htmlFor={id("travel_date")} required error={errors.travel_date}>
			<DateInput
				id={id("travel_date")}
				value={value.travel_date}
				invalid={!!errors.travel_date}
				className={inputClass(!!errors.travel_date)}
				onChange={(v) => setDate(v)}
				// A flight typed before the date is looked up once the date is complete.
				onBlur={() => air && value.service_no.trim() && !value.flight_id && value.travel_date && void runLookup(value.travel_date)}
				onKeyDown={
					air
						? (e) => {
								if (e.key !== "Enter") return;
								e.preventDefault(); // as on the flight number: look it up, never submit the sheet
								if (value.service_no.trim() && value.travel_date) void runLookup(value.travel_date);
							}
						: undefined
				}
			/>
		</F>
	);

	// The flight's departure date, picked from the days it flies. A button (never typed) showing the
	// pick; its id is the leg's `travel_date` so a failed save focuses it.
	const pickerDateField = (() => {
		if (!dateMode || dateMode.kind === "typed") return null;
		const fixed = dateMode.kind === "fixed";
		const waiting = dateMode.kind === "await" || dateMode.kind === "loading";
		const text = pickedDay ? formatDayShort(pickedDay) : dateMode.kind === "await" ? t("legForm.dates.awaitFlight") : dateMode.kind === "loading" ? t("legForm.dates.loading") : t("legForm.dates.pick");
		const aside = picking && lookupState.kind === "loading" ? <LookupSpinner /> : fixed ? (
			<button
				type="button"
				onClick={() => setManual({ key: designatorKey(value.service_no), reason: dateMode.reason })}
				className="cursor-pointer rounded-[0.25rem] text-[0.8125rem] font-semibold text-gms-navy hover:underline hover:underline-offset-[3px] focus-visible:outline-2 focus-visible:outline-gms-navy"
			>
				{t("legForm.dates.change")}
			</button>
		) : undefined;
		return (
			<F
				label={arrival ? t("legForm.field.flightDateDeparts") : t("legForm.field.flightDate")}
				htmlFor={id("travel_date")}
				required
				error={errors.travel_date}
				aside={aside}
				hint={fixed ? t("legForm.dates.fixed") : arrival ? t("legForm.dates.hintArrival") : undefined}
			>
				<button
					ref={dateButton}
					id={id("travel_date")}
					type="button"
					disabled={waiting && !pickedDay}
					aria-disabled={fixed || undefined}
					aria-expanded={fixed || waiting ? undefined : showCalendar}
					aria-controls={fixed || waiting ? undefined : id("calendar")}
					aria-invalid={!!errors.travel_date || undefined}
					aria-describedby={errors.travel_date ? `${id("travel_date")}-error` : undefined}
					onClick={fixed || waiting ? undefined : openCalendar}
					className={inputClass(
						!!errors.travel_date,
						`flex items-center justify-between gap-[0.5rem] text-left ${fixed ? "cursor-default bg-gms-soft" : "cursor-pointer"} disabled:cursor-not-allowed disabled:bg-gms-soft disabled:text-gms-ink-3`,
					)}
				>
					<span className={`truncate ${pickedDay ? "font-medium text-gms-navy-ink" : "text-[#9a9cb2]"}`}>{text}</span>
					{dateMode.kind === "loading" ? (
						<span className="loading loading-spinner size-[0.875rem] text-gms-ink-3" aria-hidden />
					) : (
						<svg viewBox="0 0 24 24" aria-hidden className="size-[1.0625rem] shrink-0 text-gms-ink-3" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
							<rect x="3.5" y="5" width="17" height="15" rx="2" />
							<path d="M3.5 10h17M8 3v4M16 3v4" />
						</svg>
					)}
				</button>
			</F>
		);
	})();

	const timeField = (
		<F
			label={arrival ? t("legForm.field.arrivalTime", { zone }) : t("legForm.field.departureTime", { zone })}
			htmlFor={id("scheduled_at")}
			error={errors.scheduled_at}
			hint={time && !value.travel_date ? t("legForm.hint.timeNeedsDate") : t("legForm.hint.timeOptional")}
		>
			<TextInput id={id("scheduled_at")} type="time" value={time} invalid={!!errors.scheduled_at} onChange={(e) => setTimeText(e.target.value)} />
		</F>
	);

	const looking = air && (dateMode?.kind === "loading" || (lookupState.kind === "loading" && !picking));
	const serviceInput = (
		<TextInput
			id={id("service_no")}
			value={value.service_no}
			invalid={!!(errors.service_no ?? errors.flight_id)}
			maxLength={TRAVEL_SERVICE_NO_MAX}
			autoComplete="off"
			spellCheck={false}
			placeholder={servicePlaceholder}
			className={air ? "pr-[2.75rem]" : ""}
			onChange={(e) => {
				const service_no = air ? e.target.value.toUpperCase() : e.target.value;
				if (!air) return set({ service_no });
				// Another number is another flight: what was found no longer describes it, and the date,
				// time and origin it filled were its own (kept when they were typed by hand).
				const fromFlight = !!value.flight_id && !typedDate;
				if (value.flight_id || lookupState.kind !== "idle") reset();
				pickSeq.current++;
				setPicking(null);
				setPickMiss(null);
				setCalendarOpen(false);
				if (fromFlight) setTime("");
				set({ service_no, flight_id: null, flight: null, ...(fromFlight ? { travel_date: "", scheduled_at: null, origin: "" } : {}) });
			}}
			onBlur={commitFlight}
			onKeyDown={air ? onFlightKey : undefined}
		/>
	);

	const serviceField = (
		<F label={serviceLabel} htmlFor={id("service_no")} required={air} error={errors.service_no ?? errors.flight_id} aside={looking ? <LookupSpinner /> : undefined}>
			{air ? (
				// AIR: what Enter / blur does, as a button at the field's right edge.
				<div className="relative min-w-0">
					{serviceInput}
					<button
						type="button"
						aria-label={t("legForm.lookup.button")}
						title={t("legForm.lookup.button")}
						disabled={disabled || !value.service_no.trim()}
						aria-busy={looking || undefined}
						// Keep the focus in the field: a blur would commit first, then this click a second time.
						onMouseDown={(e) => e.preventDefault()}
						onClick={commitFlight}
						className="absolute inset-y-0 right-[0.375rem] my-auto inline-flex size-[2.25rem] cursor-pointer items-center justify-center rounded-[0.375rem] text-gms-ink-3 transition-colors hover:bg-gms-soft hover:text-gms-navy focus-visible:outline-2 focus-visible:outline-gms-navy disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gms-ink-3"
					>
						{looking ? <span className="loading loading-spinner size-[0.9375rem]" aria-hidden /> : <Icons.Search className="size-[1.0625rem]" />}
					</button>
				</div>
			) : (
				serviceInput
			)}
		</F>
	);

	const originField = (
		<F label={arrival ? t("legForm.field.from") : t("legForm.field.to")} htmlFor={id("origin")} error={errors.origin}>
			{value.mode === "AIR" ? (
				// An airport is a shortcut to the same text (`City (IATA)`); anything typed is kept as it is.
				<AirportSelect
					id={id("origin")}
					value={value.origin}
					invalid={!!errors.origin}
					maxLength={TRAVEL_ORIGIN_MAX}
					placeholder={arrival ? t("legForm.placeholder.fromAirport") : t("legForm.placeholder.toAirport")}
					disabled={disabled}
					className={inputClass(!!errors.origin)}
					onChange={(origin) => set({ origin })}
				/>
			) : (
				<TextInput
					id={id("origin")}
					value={value.origin}
					invalid={!!errors.origin}
					maxLength={TRAVEL_ORIGIN_MAX}
					placeholder={arrival ? t("legForm.placeholder.from") : t("legForm.placeholder.to")}
					onChange={(e) => set({ origin: e.target.value })}
				/>
			)}
		</F>
	);

	// The main flight: lands at (ARRIVAL) / leaves from (DEPARTURE) a Games airport.
	const mainFlight = (
		<>
			{flightFieldsShown && (
				<Pair>
					{serviceField}
					{typedDate ? typedDateField : pickerDateField}
				</Pair>
			)}
			{showCalendar && dateMode?.kind === "picker" && (
				<div className="-mt-[0.25rem] grid justify-items-center gap-[0.5rem] rounded-field border border-gms-line bg-base-100 px-[0.75rem] pt-[0.625rem] pb-[0.75rem]">
					<DatePickerCalendar
						id={id("calendar")}
						value={pickedDay}
						enabled={dateMode.dates}
						today={today}
						startFrom={transit ? calendarStartFor(chain, mainPos, toDay) : null}
						autoFocus={calendarFocus}
						label={t("legForm.dates.calendarLabel", { flight: value.service_no.trim() })}
						onSelect={(d) => void pickDay(d)}
						onEscape={() => {
							setCalendarOpen(false);
							dateButton.current?.focus();
						}}
					/>
					<p className="text-center text-[0.75rem] leading-[1.4] text-gms-ink-3">{t("legForm.dates.legend", { flight: value.service_no.trim() })}</p>
				</div>
			)}
			{value.flight ? (
				<FlightCard
					direction={direction}
					flight={value.flight}
					entryPoint={value.entry_point}
					timeZone={timeZone}
					games
					action={
						flightFieldsShown ? undefined : (
							<LinkButton onClick={changeFlight} disabled={disabled}>
								{t("legForm.dates.change")}
							</LinkButton>
						)
					}
				/>
			) : (
				<FlightBox
					direction={direction}
					draft={value}
					state={lookupState}
					dateMode={dateMode}
					picking={!!picking}
					onEnterYourself={enterYourself}
					onConnect={!transit && notOurAirport && canAddConnection(value) && !disabled ? startTransit : undefined}
				/>
			)}
			{/* Closed, the fields stay mounted (hidden) so their ids and values are always there. */}
			<div id={id("details")} hidden={!showDetails} className="grid min-w-0 gap-[0.875rem]">
				{entryField}
				{/* On a flight, time and origin are the flight's: shown on the card, never typed. */}
				{!value.flight && (
					<Pair>
						{timeField}
						{originField}
					</Pair>
				)}
			</div>
		</>
	);

	// The carriers of every flight card in this leg (main + connections): ONE logos request for the set.
	const airlineLogos = useAirlineLogos(air ? [value.flight?.carrier_code, ...value.connections.map((c) => c.flight?.carrier_code)] : []);

	/** "1st flight" … "3rd flight": the flight's place in travel order (1-based). */
	const ordinal = (n: number) => (n <= 1 ? t("legForm.transit.ordinal.1") : n === 2 ? t("legForm.transit.ordinal.2") : t("legForm.transit.ordinal.3"));

	/** Connection `index` on its rail: "1st flight", its row, the message for its position. */
	const connectionStep = (c: ConnectionDraft, index: number, last = false) => {
		const pos = connectionPosition(direction, index);
		const key = chainErrorKey(pos);
		return (
			<ChainStep
				label={ordinal(pos + 1)}
				last={last}
				action={
					<LinkButton onClick={() => removeConnection(index)} label={t("legForm.transit.removeLabel", { flight: ordinal(pos + 1) })} disabled={disabled}>
						{t("legForm.transit.remove")}
					</LinkButton>
				}
			>
				<ConnectionRow
					direction={direction}
					row={c}
					idBase={id(key)}
					timeZone={timeZone}
					startFrom={calendarStartFor(chain, pos, toDay)}
					invalid={!!errors[key]}
					disabled={disabled}
					onChange={updateConnection}
				/>
				<ChainError id={id(key)} error={errors[key]} />
			</ChainStep>
		);
	};

	const addButton = canAddConnection(value) ? (
		<ChainGap end={direction === "DEPARTURE"}>
			<LinkButton onClick={addConnection} disabled={disabled}>
				<Icons.Plus className="size-[0.875rem]" />
				{t("legForm.transit.add")}
			</LinkButton>
		</ChainGap>
	) : null;

	return (
		<AirlineLogosProvider logos={airlineLogos}>
			<fieldset disabled={disabled} className="grid min-w-0 gap-[0.875rem]">
				<F label={t("legForm.field.mode")} htmlFor={id("mode")} required error={errors.mode}>
					<ModeSwitch id={id("mode")} value={value.mode} onChange={setMode} label={t("legForm.field.mode")} invalid={!!errors.mode} />
				</F>

				{value.mode && (
					<F label={t("legForm.field.transportType")} htmlFor={id("transport_type")} required error={errors.transport_type}>
						<TransportTypeSwitch
							id={id("transport_type")}
							types={TRAVEL_TRANSPORT_TYPES_BY_MODE[value.mode]}
							value={value.transport_type}
							onChange={setTransportType}
							label={t("legForm.field.transportType")}
							invalid={!!errors.transport_type}
						/>
					</F>
				)}
				{value.transport_type === "OTHER" && (
					<F label={t("legForm.field.transportTypeOther")} htmlFor={id("transport_type_other")} required error={errors.transport_type_other}>
						<TextInput
							id={id("transport_type_other")}
							value={value.transport_type_other}
							maxLength={TRAVEL_TRANSPORT_TYPE_OTHER_MAX}
							invalid={!!errors.transport_type_other}
							placeholder={t("legForm.placeholder.transportTypeOther")}
							onChange={(e) => set({ transport_type_other: e.target.value })}
						/>
					</F>
				)}

				{air ? (
					<>
						{transit ? (
							<div className="grid min-w-0 gap-[0.875rem]">
								<ChainError id={id("connections")} error={errors.connections} />
								{arrival ? (
									<>
										{value.connections.map((c, i) => (
											<Fragment key={c.key}>
												{connectionStep(c, i)}
												{c.flight && chain[i + 1]?.flight && (
													<ChainGap>
														<Junction prev={c.flight} next={chain[i + 1]!.flight} quiet={!!errors[chainErrorKey(i + 1)]} />
													</ChainGap>
												)}
											</Fragment>
										))}
										{addButton}
										<ChainStep label={t("legForm.transit.finalArrival")} last>
											{mainFlight}
											<ChainError id={id(chainErrorKey(mainPos))} error={errors[chainErrorKey(mainPos)]} />
										</ChainStep>
									</>
								) : (
									<>
										<ChainStep label={ordinal(1)}>
											{mainFlight}
											<ChainError id={id(chainErrorKey(mainPos))} error={errors[chainErrorKey(mainPos)]} />
										</ChainStep>
										{value.connections.map((c, i) => (
											<Fragment key={c.key}>
												{chain[i]?.flight && c.flight && (
													<ChainGap>
														<Junction prev={chain[i]!.flight} next={c.flight} quiet={!!errors[chainErrorKey(i + 1)]} />
													</ChainGap>
												)}
												{connectionStep(c, i, i === value.connections.length - 1 && !canAddConnection(value))}
											</Fragment>
										))}
										{addButton}
									</>
								)}
							</div>
						) : (
							<>
								{mainFlight}
								{offersFirstConnection(value) && (
									<LinkButton onClick={startTransit} disabled={disabled}>
										<Icons.Plus className="size-[0.875rem]" />
										{t("legForm.transit.addFirst")}
									</LinkButton>
								)}
							</>
						)}
					</>
				) : (
					<>
						{entryField}
						<Pair>
							{typedDateField}
							{timeField}
						</Pair>
						<Pair>
							{serviceField}
							{originField}
						</Pair>
					</>
				)}

				<SwitchRow
					id={id("needs_transport")}
					label={arrival ? t("legForm.field.transferArrival") : t("legForm.field.transferDeparture")}
					hint={arrival ? t("legForm.hint.transferArrival") : t("legForm.hint.transferDeparture")}
					checked={value.needs_transport}
					onChange={(v) => set({ needs_transport: v })}
				/>

				{showRemarks ? (
					<F label={t("legForm.field.remarks")} htmlFor={id("remarks")} error={errors.remarks}>
						<TextArea
							id={id("remarks")}
							rows={2}
							value={value.remarks}
							maxLength={TRAVEL_REMARKS_MAX}
							invalid={!!errors.remarks}
							placeholder={t("legForm.placeholder.remarks")}
							autoFocus={remarksFocus}
							onChange={(e) => set({ remarks: e.target.value })}
						/>
					</F>
				) : (
					<LinkButton
						onClick={() => {
							setRemarksFocus(true);
							setRemarksOpen(true);
						}}
						disabled={disabled}
					>
						<Icons.Plus className="size-[0.875rem]" />
						{t("legForm.addRemark")}
					</LinkButton>
				)}
			</fieldset>
		</AirlineLogosProvider>
	);
}

/** Within the mode: Commercial flight / Private / Cargo / Others… — chips, one picked. */
function TransportTypeSwitch({
	id,
	types,
	value,
	onChange,
	label,
	invalid,
}: {
	id: string;
	types: readonly TravelTransportType[];
	value: TravelTransportType | null;
	onChange: (v: TravelTransportType) => void;
	label: string;
	invalid: boolean;
}) {
	const { t } = useTranslation("travel");
	return (
		<div id={id} role="radiogroup" aria-label={label} className={`flex flex-wrap gap-[0.375rem] rounded-field ${invalid ? "shadow-[0_0_0_1px_var(--color-gms-bad)]" : ""}`}>
			{types.map((v) => {
				const on = v === value;
				return (
					<button
						key={v}
						type="button"
						role="radio"
						aria-checked={on}
						onClick={() => onChange(v)}
						className={`min-h-[2.25rem] cursor-pointer rounded-full border px-[0.875rem] text-[0.8125rem] transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${
							on ? "border-gms-navy bg-gms-navy-soft font-semibold text-gms-navy" : "border-gms-line-2 bg-base-100 font-medium text-gms-ink-2 hover:border-gms-navy hover:text-gms-navy-ink"
						}`}
					>
						{t(`legForm.transportType.${v}`)}
					</button>
				);
			})}
		</div>
	);
}

/** Air / Land / Sea — her segmented control with the mode glyphs (`.seg`). */
function ModeSwitch({ id, value, onChange, label, invalid }: { id: string; value: TravelMode | ""; onChange: (m: TravelMode) => void; label: string; invalid: boolean }) {
	const { t } = useTranslation("travel");
	return (
		<div
			id={id}
			role="radiogroup"
			aria-label={label}
			className={`grid grid-cols-3 gap-[0.25rem] rounded-field bg-gms-soft-2 p-[0.25rem] ${invalid ? "shadow-[0_0_0_1px_var(--color-gms-bad)]" : ""}`}
		>
			{TRAVEL_MODES.map((m) => {
				const on = m === value;
				return (
					<button
						key={m}
						type="button"
						role="radio"
						aria-checked={on}
						onClick={() => onChange(m)}
						className={`inline-flex min-h-[2.5rem] items-center justify-center gap-[0.4375rem] rounded-[0.375rem] px-[0.625rem] text-[0.875rem] transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${
							on ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
						}`}
					>
						<ModeIcon mode={m} className="size-[1.0625rem]" />
						{t(`legForm.mode.${m}`)}
					</button>
				);
			})}
		</div>
	);
}

function LookupSpinner() {
	const { t } = useTranslation("travel");
	return (
		<span className="inline-flex items-center gap-[0.3125rem]" role="status">
			<span className="loading loading-spinner size-[0.75rem]" aria-hidden />
			{t("legForm.lookup.loading")}
		</span>
	);
}

/**
 * Under the flight number and date while no flight is on the leg (a found
 * one is its `FlightCard`): one quiet line — what to do next, or why the
 * details are typed by hand. A flight that does not reach a Games airport
 * gets a warning box with "+ Add a connecting flight" (`onConnect`); a picked
 * day the lookup could not answer offers "Enter times yourself".
 */
function FlightBox({
	direction,
	draft,
	state,
	dateMode,
	picking,
	onEnterYourself,
	onConnect,
}: {
	direction: TravelDirection;
	draft: TravelLegDraft;
	state: FlightLookupState;
	dateMode: AirDateMode | null;
	/** A picked day is being looked up. */
	picking: boolean;
	/** Under a picked day's miss: into the typed fallback. */
	onEnterYourself: () => void;
	/** The flight does not reach a Games airport: start a transit leg with it (no transit yet). */
	onConnect?: (() => void) | undefined;
}) {
	const { t } = useTranslation("travel");
	const arrival = direction === "ARRIVAL";
	const flightText = (() => {
		const p = parseFlightDesignator(draft.service_no.trim());
		return p ? formatFlightDesignator(p.carrier_code, p.flight_number) : draft.service_no.trim();
	})();
	const place = (name: string | null | undefined, code: string | null | undefined) =>
		name && code ? t("legForm.pickMiss.place", { name, code }) : (name ?? code ?? t("legForm.pickMiss.unknownPlace"));
	/** "Z2 502 on Sat 17 Oct flies Kota Kinabalu (BKI) → Manila (MNL). It doesn't arrive at a Games airport (KUL)." */
	const routeLine = (day: string, route: FlightLookupRoute | undefined, airports: readonly string[] | undefined) => {
		const vars = {
			flight: flightText,
			date: formatDayShort(day),
			from: place(route?.origin_name, route?.origin_iata),
			to: place(route?.destination_name, route?.destination_iata),
			airports: (airports ?? []).join(", "),
		};
		return arrival ? t("legForm.pickMiss.notOurAirportArrival", vars) : t("legForm.pickMiss.notOurAirportDeparture", vars);
	};
	/** The line, and whether it is about a flight that does not reach a Games airport. */
	const said = ((): { text: string; route: boolean } | null => {
		if (dateMode?.kind === "typed") {
			// A typed date's own lookup — only once a date was typed and looked up, and only for that date.
			const asked = state.kind === "miss" ? state.asked : undefined;
			if (state.kind === "miss" && asked?.dateKind === "LEG" && asked.date !== "" && asked.date === draft.travel_date) {
				if (state.reason === "NOT_OUR_AIRPORT") return { text: routeLine(asked.date, state.route, state.airports), route: true };
				return { text: t(`legForm.lookup.miss.${state.reason}`), route: false };
			}
			if (dateMode.reason === "MANUAL") return null;
			// Not in the schedules at all: say for which window, so it does not read like a failed lookup.
			if ((dateMode.reason === "NOT_FOUND" || dateMode.reason === "NO_DATES") && dateMode.window)
				return { text: t("legForm.dates.notInSchedules", { flight: flightText, from: formatDayShort(dateMode.window.from), to: formatDayShort(dateMode.window.to) }), route: false };
			return { text: t(`legForm.dates.miss.${dateMode.reason}`), route: false };
		}
		if (dateMode?.kind === "await") return { text: t("legForm.dates.prompt"), route: false };
		if (dateMode?.kind === "picker" && !picking) {
			const miss = dateMode.miss;
			if (!miss) return { text: t("legForm.dates.pickPrompt"), route: false };
			switch (miss.reason) {
				case "NOT_OUR_AIRPORT":
					return { text: routeLine(miss.day, miss.route, miss.airports), route: true };
				case "NOT_FOUND":
					return { text: t("legForm.pickMiss.notFound", { flight: flightText, date: formatDayShort(miss.day) }), route: false };
				case "UNAVAILABLE":
					return { text: t("legForm.pickMiss.unavailable"), route: false };
				case "RATE_LIMITED":
					return { text: t("legForm.pickMiss.rateLimited"), route: false };
			}
		}
		return null;
	})();
	if (!said) return null;
	const pickMissed = dateMode?.kind === "picker" && !picking && !!dateMode.miss;
	const enterYourself = pickMissed ? <LinkButton onClick={onEnterYourself}>{t("legForm.pickMiss.enterYourself")}</LinkButton> : null;
	if (said.route)
		return (
			<div role="status" className="grid gap-[0.375rem] rounded-field bg-gms-warn-soft px-[0.875rem] py-[0.625rem] text-[0.8125rem] leading-[1.45]">
				<p className="flex items-start gap-[0.4375rem] font-medium text-gms-warn">
					<Icons.Alert className="mt-[0.125rem] size-[0.9375rem] shrink-0" />
					<span className="min-w-0">
						{said.text}
						{onConnect && <> {arrival ? t("legForm.transit.notOurAirportHintArrival") : t("legForm.transit.notOurAirportHintDeparture")}</>}
					</span>
				</p>
				{(onConnect || enterYourself) && (
					<div className="flex flex-wrap items-baseline gap-x-[1.25rem] gap-y-[0.25rem] pl-[1.375rem]">
						{onConnect && (
							<LinkButton onClick={onConnect}>
								<Icons.Plus className="size-[0.875rem]" />
								{t("legForm.transit.addFirst")}
							</LinkButton>
						)}
						{enterYourself}
					</div>
				)}
			</div>
		);
	if (pickMissed)
		return (
			<div className="-mt-[0.375rem] grid gap-[0.25rem]">
				<p role="status" className="flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-warn">
					<Icons.Alert className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
					<span className="min-w-0">{said.text}</span>
				</p>
				<div className="pl-[1.25rem]">{enterYourself}</div>
			</div>
		);
	return <p className="-mt-[0.375rem] text-[0.78125rem] leading-[1.4] text-gms-ink-3">{said.text}</p>;
}
